'use client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import { PaginationArrow } from '@/components/ui/PaginationArrow/PaginationArrow';
import { getPageNumbersTemplate } from '@/components/features/Pagination/Pagination.utils';
import s from './Pagination.module.scss';
import { IWithClassname } from '@/models/common';

interface Props extends IWithClassname {
  totalPages: number;
}

export const Pagination = ({ totalPages, className }: Props) => {
  const pathname = usePathname();
  const { replace } = useRouter();
  const searchParams = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 1;
  const pagesTemplate: (number | string)[] = getPageNumbersTemplate(currentPage, totalPages);

  const changeCurrentPage = (pageNumber: number | string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', pageNumber.toString());
    params.delete('show_more');
    replace(`${pathname}?${params.toString()}`);
  };

  if (totalPages < 2) return null;

  return (
    <div className={clsx(s.pagination, className)}>
      <PaginationArrow
        onClick={() => changeCurrentPage(1)}
        disabled={currentPage <= 1}
        isDoubleArrow
      />
      <PaginationArrow
        onClick={() => changeCurrentPage(currentPage - 1)}
        disabled={currentPage <= 1}
      />
      <ul className={s.pagination__pages}>
        {pagesTemplate.map((pageNumber, i) => {
          if (typeof pageNumber === 'number') {
            return (
              <button
                key={`${pageNumber}${i}`}
                onClick={() => changeCurrentPage(pageNumber)}
                className={clsx(s.pagination__page, { [s.pagination__page_selected]: pageNumber === currentPage })}
              >
                {pageNumber}
              </button>
            );
          }

          return <div key={'dots'} className={s.pagination__dots}>...</div>;
        })}
      </ul>
      <PaginationArrow
        onClick={() => changeCurrentPage(currentPage + 1)}
        disabled={currentPage >= totalPages}
        reverse
      />
      <PaginationArrow
        onClick={() => changeCurrentPage(totalPages)}
        disabled={currentPage >= totalPages}
        isDoubleArrow
        reverse
      />
    </div>

  );
};